import { Cancel } from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  OutlinedTextFieldProps,
  Stack,
  Typography,
} from '@mui/material';
import { WarningIcon } from '@shared/custom-icons';
import { forwardRef, KeyboardEvent } from 'react';
import { InfoTooltip } from '../Tooltip';
import { FormControlWrapper, FormLabelWrapper, HelperText } from './Components';
import { SystemStyleObject } from '@mui/system';

export interface TextInputFieldProps
  extends Pick<
    OutlinedTextFieldProps,
    | 'onChange'
    | 'onClick'
    | 'onBlur'
    | 'name'
    | 'value'
    | 'rows'
    | 'multiline'
    | 'disabled'
    | 'onKeyDown'
    | 'defaultValue'
    | 'autoFocus'
  > {
  size?: 'small' | 'medium';
  label?: string;
  error?: string;
  placeholder?: string;
  required?: boolean;
  margin?: 'normal' | 'dense' | 'none';
  fullWidth?: boolean;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  tooltipText?: string;
  reset?: () => void;
  labelDirection?: 'row' | 'column';
  success?: string;
  labelWidth?: number;
  maxContentLength?: number;
  sx?: SystemStyleObject;
  inputSx?: SystemStyleObject;
  helperTextSx?: SystemStyleObject;
}

export const TextInputField = forwardRef(
  (
    {
      name,
      label,
      error,
      required,
      margin = 'normal',
      fullWidth,
      value,
      endAdornment,
      startAdornment,
      tooltipText,
      size = 'medium',
      reset,
      disabled,
      labelDirection = 'column',
      success,
      onKeyDown,
      labelWidth = 100,
      maxContentLength,
      sx = {},
      inputSx,
      helperTextSx = {},
      ...rest
    }: TextInputFieldProps,
    ref
  ) => {
    const inputId = `${name}-id`;
    const formLabelId = `${name}-label`;
    const helperTextId = `${name}-helperText`;

    return (
      <FormControl
        sx={{ minWidth: 100, ...sx }}
        margin={margin}
        error={!!error}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        component={FormControlWrapper}
        labelDirection={labelDirection}
      >
        {label && (
          <FormLabelWrapper direction={labelDirection} width={labelWidth} size={size}>
            <FormLabel htmlFor={inputId} id={formLabelId}>
              {label}
            </FormLabel>
            {tooltipText && <InfoTooltip tooltipText={tooltipText} disabled={disabled} />}
          </FormLabelWrapper>
        )}

        <Box flex="1 0 0" position="relative">
          <OutlinedInput
            {...rest}
            size={size}
            id={inputId}
            fullWidth={fullWidth}
            inputRef={ref}
            value={value}
            aria-describedby={helperTextId}
            sx={inputSx}
            autoComplete="off"
            startAdornment={
              startAdornment ? (
                <InputAdornment position="start">{startAdornment}</InputAdornment>
              ) : null
            }
            endAdornment={
              error ? (
                <InputAdornment position="end">
                  <WarningIcon color="warning" />
                </InputAdornment>
              ) : value && reset ? (
                <InputAdornment position="end">
                  <IconButton onClick={reset} sx={{ padding: 0 }}>
                    <Cancel color="disabled" />
                  </IconButton>
                </InputAdornment>
              ) : endAdornment ? (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              ) : null
            }
            inputProps={{
              onKeyDown: onKeyDown
                ? (event: KeyboardEvent<HTMLInputElement>) => onKeyDown(event)
                : undefined,
            }}
          />
          <Stack
            direction={maxContentLength ? 'row-reverse' : 'row'}
            justifyContent="space-between"
            alignItems="center"
            sx={helperTextSx}
          >
            {maxContentLength && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 1, px: 0.5, whiteSpace: 'nowrap' }}
              >
                {`${(value as string)?.length ?? 0} / ${maxContentLength}`}
              </Typography>
            )}
            {error && (
              <HelperText id={helperTextId} status={'error'}>
                {error}
              </HelperText>
            )}

            {success && (
              <HelperText id={helperTextId} status={'success'}>
                {success}
              </HelperText>
            )}
          </Stack>
        </Box>
      </FormControl>
    );
  }
);
